import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { toaster } from '@/components/ui/toaster'
import { WBDApi } from '@/open-api'
import type {
  CollectionItemDto,
  CreateCollectionDto,
  GetCollectionResponseDto,
  GetCollectionsResponseDto,
  UpdateCollectionDto
} from '@/open-api/Api'

export type Collection = CollectionItemDto
export type CollectionDetail = GetCollectionResponseDto
export type CollectionsResponse = GetCollectionsResponseDto

export interface GetCollectionsQueryDto {
  skip?: number
  take?: number
  searchTerm?: string
}

const COLLECTIONS_QUERY_KEY = 'collections'

export const useCollection = (id: string) => {
  return useQuery({
    queryKey: [COLLECTIONS_QUERY_KEY, 'details', id],
    queryFn: () =>
      WBDApi.collections
        .collectionControllerGetCollection(id)
        .then(({ data }) => data)
  })
}

export const useCollections = (query: GetCollectionsQueryDto) => {
  return useQuery({
    queryKey: [COLLECTIONS_QUERY_KEY, query],
    queryFn: () =>
      WBDApi.collections
        .collectionControllerGetCollections({
          skip: query.skip ?? 0,
          take: query.take ?? 9,
          searchTerm: query.searchTerm
        })
        .then(({ data }) => data)
  })
}

const ITEMS_PER_PAGE = 9

export const useInfiniteCollections = (filters: { searchTerm?: string }) => {
  return useInfiniteQuery({
    queryKey: [COLLECTIONS_QUERY_KEY, filters],
    queryFn: ({ pageParam }) =>
      WBDApi.collections
        .collectionControllerGetCollections({
          skip: pageParam,
          take: ITEMS_PER_PAGE,
          searchTerm: filters.searchTerm
        })
        .then(({ data }) => data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.items.length,
        0
      )
      if (totalFetched >= lastPage.count) {
        return undefined
      }
      return totalFetched
    }
  })
}

export const useCreateCollection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateCollectionDto) =>
      WBDApi.collections
        .collectionControllerCreateCollection(payload)
        .then(({ data }) => data),
    onSuccess: () => {
      toaster.create({
        title: 'Collection created',
        type: 'success'
      })
      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to create collection',
        type: 'error'
      })
    }
  })
}

export const useUpdateCollection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateCollectionDto & { id: string }) =>
      WBDApi.collections
        .collectionControllerUpdateCollection(id, payload)
        .then(({ data }) => data),
    onSuccess: () => {
      toaster.create({
        title: 'Collection updated',
        type: 'success'
      })
      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to update collection',
        type: 'error'
      })
    }
  })
}

export const useDeleteCollection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      WBDApi.collections
        .collectionControllerDeleteCollection(id)
        .then(({ data }) => data),
    onSuccess: () => {
      toaster.create({
        title: 'Collection deleted',
        type: 'success'
      })
      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to delete collection',
        type: 'error'
      })
    }
  })
}
